import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { FaGlobe, FaCheck } from "react-icons/fa";

const LanguageSwitcher = () => {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧", nativeName: "English" },
    { code: "hi", name: "Hindi", flag: "🇮🇳", nativeName: "हिंदी" },
    { code: "mr", name: "Marathi", flag: "🇮🇳", nativeName: "मराठी" },
    { code: "gu", name: "Gujarati", flag: "🇮🇳", nativeName: "ગુજરાતી" },
    { code: "ta", name: "Tamil", flag: "🇮🇳", nativeName: "தமிழ்" },
    { code: "te", name: "Telugu", flag: "🇮🇳", nativeName: "తెలుగు" },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === language) || languages[0];

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      {/* Language Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          padding: "8px 12px",
          background: "white",
          border: "1.5px solid rgba(238, 46, 36, 0.2)",
          borderRadius: "10px",
          color: "#2E2E2E",
          fontSize: "0.85rem",
          fontWeight: "700",
          cursor: "pointer",
          transition: "all 0.2s ease",
          fontFamily: "'Inter', sans-serif",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          width: "100%",
          maxWidth: "110px",
          margin: "0 auto",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#EE2E24";
          e.currentTarget.style.background = "rgba(238, 46, 36, 0.05)";
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow =
            "0 4px 12px rgba(238, 46, 36, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "rgba(238, 46, 36, 0.2)";
          e.currentTarget.style.background = "white";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
        }}
      >
        <span style={{ fontSize: "1.1rem" }}>{currentLanguage.flag}</span>
        <span>{currentLanguage.code.toUpperCase()}</span>
        <span style={{ fontSize: "0.7rem", opacity: 0.5, marginLeft: "auto" }}>▼</span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            // Position on the left for mobile to prevent overflow
            left: 0,
            right: isMobile ? "auto" : 0,
            background: "white",
            borderRadius: "14px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            minWidth: isMobile ? "160px" : "180px",
            maxWidth: "240px",
            zIndex: 10000,
            overflow: "hidden",
            animation: "slideDown 0.25s cubic-bezier(0, 0, 0.2, 1)",
          }}
        >
          {/* Dropdown Header */}
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
              background: "#FFFBFB",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "0.68rem",
                fontWeight: "800",
                color: "#EE2E24",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                opacity: 0.8,
              }}
            >
              Select Language
            </p>
          </div>

          {/* Language Options */}
          <div style={{ padding: "6px" }}>
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 12px",
                  background:
                    language === lang.code
                      ? "rgba(238, 46, 36, 0.05)"
                      : "transparent",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  fontFamily: "'Inter', sans-serif",
                  textAlign: "left",
                  position: "relative",
                  margin: "2px 0",
                }}
                onMouseEnter={(e) => {
                  if (language !== lang.code) {
                    e.currentTarget.style.background = "rgba(0, 0, 0, 0.03)";
                    e.currentTarget.style.paddingLeft = "14px";
                  }
                }}
                onMouseLeave={(e) => {
                  if (language !== lang.code) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.paddingLeft = "12px";
                  }
                }}
              >
                <span style={{ fontSize: "1.2rem" }}>{lang.flag}</span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "0.82rem",
                      fontWeight: "700",
                      color: language === lang.code ? "#EE2E24" : "#2E2E2E",
                    }}
                  >
                    {lang.name}
                  </div>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      color: "#888",
                      marginTop: "1px",
                      fontWeight: "500",
                    }}
                  >
                    {lang.nativeName}
                  </div>
                </div>
                {language === lang.code && (
                  <div style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: "#EE2E24",
                    boxShadow: "0 0 8px rgba(238, 46, 36, 0.5)"
                  }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default LanguageSwitcher;
